// components/CreateCampaignModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaEnvelopeOpenText,
  FaPaperPlane,
  FaPlay,
  FaSpinner,
  FaTimes,
  FaUsers,
} from "react-icons/fa";

import {
  createEmailCampaign,
  getEmailSenders,
  getEmailTemplates,
  prepareEmailCampaignRecipients,
  sendEmailCampaignTest,
} from "../../../../../services/mailingServices";

import { PRODUCT_OPTIONS, getProductLabel } from "../constants";

const defaultLimit = 1000;

function todayLabel() {
  return new Date().toLocaleDateString("fr-FR");
}

function getDefaultName(seed, productKey) {
  const product = getProductLabel(productKey);

  if (seed?.mode === "selection") {
    return `${product} - Campagne sélection - ${todayLabel()}`;
  }

  if (seed?.mode === "filters") {
    return `${product} - Campagne filtres - ${todayLabel()}`;
  }

  return `${product} - Nouvelle campagne - ${todayLabel()}`;
}

function buildSegmentFromSeed(seed) {
  if (!seed) {
    return {
      has_email: "1",
      exclude_unsubscribed: true,
      exclude_bounced: true,
    };
  }

  if (seed.mode === "selection") {
    return {
      selected_ids: seed.selectedIds || [],
      has_email: "1",
      exclude_unsubscribed: true,
      exclude_bounced: true,
    };
  }

  if (seed.mode === "filters") {
    return {
      ...(seed.filters || {}),
      has_email: "1",
      exclude_unsubscribed: true,
      exclude_bounced: true,
    };
  }

  return {
    ...(seed.filters || {}),
    has_email: "1",
    exclude_unsubscribed: true,
    exclude_bounced: true,
  };
}

function getSeedLabel(seed) {
  if (!seed) return "Création manuelle";
  if (seed.mode === "selection") return "Depuis la sélection";
  if (seed.mode === "filters") return "Depuis les filtres";
  return "Création manuelle";
}

export default function CreateCampaignModal({
  campaignSeed,
  onClose,
  onCreated,
}) {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [senders, setSenders] = useState([]);

  const [createdCampaign, setCreatedCampaign] = useState(null);
  const [prepareResult, setPrepareResult] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const [form, setForm] = useState({
    name: "",
    product_key: "monappliclub",
    template_id: "",
    sender_account_id: "",
    limit: defaultLimit,
    test_email: "",
  });

  const segment = useMemo(
    () => buildSegmentFromSeed(campaignSeed),
    [campaignSeed]
  );

  const filteredTemplates = useMemo(() => {
    const byProduct = templates.filter(
      (tpl) => tpl.product_key === form.product_key
    );

    return byProduct.length > 0 ? byProduct : templates;
  }, [templates, form.product_key]);

  const selectedTemplate = useMemo(
    () =>
      templates.find((tpl) => Number(tpl.id) === Number(form.template_id)) ||
      null,
    [templates, form.template_id]
  );

  const selectedSender = useMemo(
    () =>
      senders.find((sender) => Number(sender.id) === Number(form.sender_account_id)) ||
      null,
    [senders, form.sender_account_id]
  );

  const selectedCount = Array.isArray(campaignSeed?.selectedIds)
    ? campaignSeed.selectedIds.length
    : 0;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const [tplRows, senderRows] = await Promise.all([
          getEmailTemplates({ status: "active" }),
          getEmailSenders(),
        ]);

        const safeTemplates = tplRows || [];
        const safeSenders = senderRows || [];

        setTemplates(safeTemplates);
        setSenders(safeSenders);

        const firstTemplate =
          safeTemplates.find((tpl) => tpl.product_key === "monappliclub") ||
          safeTemplates[0] ||
          null;

        const defaultSender =
          safeSenders.find((sender) => sender.is_default || sender.is_default === 1) ||
          safeSenders[0] ||
          null;

        const productKey = firstTemplate?.product_key || "monappliclub";

        setForm((prev) => ({
          ...prev,
          name: getDefaultName(campaignSeed, productKey),
          product_key: productKey,
          template_id: firstTemplate?.id || "",
          sender_account_id: defaultSender?.id || "",
        }));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [campaignSeed]);

  useEffect(() => {
    if (!filteredTemplates.length) return;

    const currentStillValid = filteredTemplates.some(
      (tpl) => Number(tpl.id) === Number(form.template_id)
    );

    if (!currentStillValid) {
      setForm((prev) => ({
        ...prev,
        template_id: filteredTemplates[0].id,
      }));
    }
  }, [filteredTemplates, form.template_id]);

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCreateAndPrepare = async () => {
    if (!form.name.trim()) {
      alert("Le nom de la campagne est requis.");
      return;
    }

    if (!form.template_id) {
      alert("Veuillez sélectionner un modèle d’email.");
      return;
    }

    try {
      setCreating(true);
      setCreatedCampaign(null);
      setPrepareResult(null);
      setTestResult(null);

      const campaign = await createEmailCampaign({
        name: form.name,
        product_key: form.product_key,
        template_id: Number(form.template_id),
        sender_account_id: form.sender_account_id
          ? Number(form.sender_account_id)
          : null,
        segment,
      });

      if (!campaign?.id) {
        throw new Error("Création de campagne impossible.");
      }

      setCreatedCampaign(campaign);

      const prepared = await prepareEmailCampaignRecipients(campaign.id, {
        limit: Number(form.limit || defaultLimit),
      });

      setPrepareResult(prepared);

      if (typeof onCreated === "function") {
        onCreated({
          campaign,
          prepared,
          close: false,
        });
      }
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la création ou de la préparation de la campagne.");
    } finally {
      setCreating(false);
    }
  };

  const handleSendTest = async () => {
    if (!createdCampaign?.id) {
      alert("Créez d’abord la campagne.");
      return;
    }

    if (!form.test_email.trim()) {
      alert("Saisissez une adresse email de test.");
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);

      const result = await sendEmailCampaignTest(createdCampaign.id, {
        to: form.test_email.trim(),
      });

      setTestResult(result || { ok: false });
    } catch (error) {
      console.error(error);
      setTestResult({
        ok: false,
        message: "Erreur lors de l’envoi du test.",
      });
    } finally {
      setTesting(false);
    }
  };

  const handleFinish = () => {
    if (typeof onCreated === "function") {
      onCreated({
        campaign: createdCampaign,
        prepared: prepareResult,
        close: true,
      });
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />

      <div className="relative w-[96vw] max-w-6xl max-h-[92vh] overflow-hidden rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 dark:border-slate-800 p-5">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200 px-3 py-1 text-xs font-medium">
              <FaPaperPlane />
              Nouvelle campagne
            </div>

            <h2 className="mt-3 text-xl font-semibold">
              Créer et préparer une campagne email
            </h2>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Choisissez le modèle, l’expéditeur et la cible. La campagne sera
              créée en brouillon puis les destinataires seront préparés.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <FaTimes />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <FaSpinner className="animate-spin mr-2" />
            Chargement des modèles et expéditeurs…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-0 max-h-[calc(92vh-112px)] overflow-auto">
            <div className="p-5 space-y-5">
              <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
                <div className="font-semibold">1. Configuration</div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Nom de la campagne" className="md:col-span-2">
                    <input
                      value={form.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Produit présenté">
                    <select
                      value={form.product_key}
                      onChange={(e) => {
                        const productKey = e.target.value;
                        updateField("product_key", productKey);

                        const first = templates.find(
                          (tpl) => tpl.product_key === productKey
                        );

                        if (first) {
                          setForm((prev) => ({
                            ...prev,
                            product_key: productKey,
                            template_id: first.id,
                            name: getDefaultName(campaignSeed, productKey),
                          }));
                        }
                      }}
                      className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                    >
                      {PRODUCT_OPTIONS.map((product) => (
                        <option key={product.value} value={product.value}>
                          {product.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Limite de préparation">
                    <input
                      type="number"
                      min="1"
                      max="5000"
                      value={form.limit}
                      onChange={(e) => updateField("limit", e.target.value)}
                      className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Modèle d’email" className="md:col-span-2">
                    <select
                      value={form.template_id}
                      onChange={(e) => updateField("template_id", e.target.value)}
                      className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                    >
                      {!filteredTemplates.length && (
                        <option value="">Aucun modèle disponible</option>
                      )}

                      {filteredTemplates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>
                          {tpl.name} — {tpl.subject}
                        </option>
                      ))}
                    </select>

                    {selectedTemplate && (
                      <div className="mt-2 rounded-2xl bg-slate-50 dark:bg-slate-900/70 p-3 text-xs text-slate-600 dark:text-slate-300">
                        <div>
                          <strong>Sujet :</strong> {selectedTemplate.subject}
                        </div>
                        {selectedTemplate.preheader ? (
                          <div className="mt-1">
                            <strong>Préheader :</strong>{" "}
                            {selectedTemplate.preheader}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </Field>

                  <Field label="Compte expéditeur" className="md:col-span-2">
                    <select
                      value={form.sender_account_id}
                      onChange={(e) =>
                        updateField("sender_account_id", e.target.value)
                      }
                      className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                    >
                      {!senders.length && (
                        <option value="">Aucun expéditeur disponible</option>
                      )}

                      {senders.map((sender) => (
                        <option key={sender.id} value={sender.id}>
                          {sender.label} — {sender.from_name} &lt;
                          {sender.from_email}&gt;
                        </option>
                      ))}
                    </select>

                    {selectedSender && (
                      <div className="mt-2 rounded-2xl bg-slate-50 dark:bg-slate-900/70 p-3 text-xs text-slate-600 dark:text-slate-300">
                        SMTP : {selectedSender.smtp_host || "—"}:
                        {selectedSender.smtp_port || "—"} • Limite jour :{" "}
                        {selectedSender.daily_limit || "—"}
                      </div>
                    )}
                  </Field>
                </div>
              </section>

              <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
                <div className="font-semibold">2. Ciblage</div>

                <div className="mt-4 grid gap-3">
                  <TargetSummary campaignSeed={campaignSeed} segment={segment} />
                </div>
              </section>

              {createdCampaign && (
                <section className="rounded-3xl border border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30 p-4">
                  <div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-100">
                    <FaCheckCircle />
                    Campagne créée
                  </div>

                  <div className="mt-2 text-sm text-emerald-900 dark:text-emerald-100/90">
                    Campagne #{createdCampaign.id} : {createdCampaign.name}
                  </div>

                  {prepareResult && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <ResultBox
                        label="Prospects trouvés"
                        value={prepareResult.total_prospects_found}
                      />
                      <ResultBox
                        label="Destinataires préparés"
                        value={prepareResult.total_recipients_prepared}
                      />
                      <ResultBox
                        label="Exclus / désinscrits"
                        value={prepareResult.excluded_unsubscribed}
                      />
                    </div>
                  )}
                </section>
              )}

              {createdCampaign && (
                <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
                  <div className="font-semibold">3. Email de test</div>

                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Avant de lancer une campagne, envoie-toi toujours un test.
                  </p>

                  <div className="mt-4 flex flex-col md:flex-row gap-2">
                    <input
                      value={form.test_email}
                      onChange={(e) => updateField("test_email", e.target.value)}
                      placeholder="ton-email@exemple.fr"
                      className="flex-1 rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                    />

                    <button
                      type="button"
                      onClick={handleSendTest}
                      disabled={testing || !form.test_email}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 text-white px-4 py-2.5 text-sm disabled:opacity-50"
                    >
                      {testing ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <FaEnvelopeOpenText />
                      )}
                      Envoyer test
                    </button>
                  </div>

                  {testResult && (
                    <div
                      className={[
                        "mt-3 rounded-2xl p-3 text-sm",
                        testResult.ok
                          ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100"
                          : "bg-rose-50 text-rose-800 dark:bg-rose-950/30 dark:text-rose-100",
                      ].join(" ")}
                    >
                      {testResult.ok
                        ? "Email de test envoyé avec succès."
                        : testResult.message || "L’email de test n’a pas pu être envoyé."}
                    </div>
                  )}
                </section>
              )}
            </div>

            <aside className="border-t lg:border-t-0 lg:border-l border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-5">
              <div className="sticky top-0 space-y-4">
                <SideCard
                  title="Source"
                  icon={<FaUsers />}
                  value={getSeedLabel(campaignSeed)}
                  detail={
                    campaignSeed?.mode === "selection"
                      ? `${selectedCount} prospect(s) sélectionné(s)`
                      : "Segment basé sur les filtres"
                  }
                />

                <SideCard
                  title="Produit"
                  value={getProductLabel(form.product_key)}
                  detail={selectedTemplate?.name || "Modèle non sélectionné"}
                />

                <SideCard
                  title="Expéditeur"
                  value={selectedSender?.from_email || "—"}
                  detail={selectedSender?.from_name || "Aucun expéditeur"}
                />

                <div className="rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4">
                  <div className="font-semibold">Actions</div>

                  <div className="mt-4 space-y-2">
                    <button
                      type="button"
                      onClick={handleCreateAndPrepare}
                      disabled={
                        creating ||
                        !form.template_id ||
                        !form.sender_account_id ||
                        !!createdCampaign
                      }
                      className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-4 py-3 text-sm font-medium disabled:opacity-50"
                    >
                      {creating ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <FaPaperPlane />
                      )}
                      Créer et préparer
                    </button>

                    <button
                      type="button"
                      disabled={!createdCampaign}
                      onClick={handleFinish}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 text-white px-4 py-3 text-sm font-medium disabled:opacity-50"
                    >
                      <FaPlay />
                      Terminer
                    </button>

                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full rounded-2xl bg-slate-100 dark:bg-slate-800 px-4 py-3 text-sm"
                    >
                      Fermer
                    </button>
                  </div>

                  <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    Le bouton “Créer et préparer” n’envoie pas encore la
                    campagne. Il crée la campagne et prépare la file de
                    destinataires. Le lancement se fera ensuite depuis la liste.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
        {label}
      </div>
      {children}
    </label>
  );
}

function TargetSummary({ campaignSeed, segment }) {
  const entries = Object.entries(segment || {}).filter(
    ([key, value]) =>
      value !== undefined &&
      value !== null &&
      value !== "" &&
      key !== "exclude_unsubscribed" &&
      key !== "exclude_bounced"
  );

  return (
    <div className="rounded-2xl bg-slate-50 dark:bg-slate-900/70 p-4">
      <div className="text-sm font-medium">{getSeedLabel(campaignSeed)}</div>

      {campaignSeed?.mode === "selection" ? (
        <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {campaignSeed.selectedIds?.length || 0} prospect(s) sélectionné(s).
        </div>
      ) : entries.length ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {entries.map(([key, value]) => (
            <span
              key={key}
              className="rounded-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-1 text-xs"
            >
              {key} : {Array.isArray(value) ? value.join(", ") : String(value)}
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-2 text-sm text-slate-500">
          Aucun filtre spécifique. Tous les prospects avec email seront ciblés,
          hors désinscrits et emails invalides.
        </div>
      )}

      <div className="mt-3 text-xs text-emerald-700 dark:text-emerald-300">
        Les désinscrits, bounces et emails invalides sont exclus côté serveur.
      </div>
    </div>
  );
}

function ResultBox({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/70 dark:bg-slate-950/50 p-3">
      <div className="text-xs opacity-75">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">
        {value ?? 0}
      </div>
    </div>
  );
}

function SideCard({ title, value, detail, icon }) {
  return (
    <div className="rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500 dark:text-slate-400">{title}</div>
        {icon ? <div className="text-slate-400">{icon}</div> : null}
      </div>

      <div className="mt-2 font-semibold break-words">{value}</div>

      {detail ? (
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 break-words">
          {detail}
        </div>
      ) : null}
    </div>
  );
}